import type { MemberDataFirestoreRecord } from "@albertine/types";
import { QuickRequestTypeLabels } from "@albertine/types";
import classNames from "classnames";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Attachment } from "../../common/types/Attachment";
import {
    MemberReaction,
    type MessageOrInternalNote,
    MessageWithArticleInquiry,
    MessageWithQuickRequest,
    ReplyToMessage,
} from "../../common/types/Message";
import type { QuickRequest } from "../../common/types/QuickRequests";
import type { MessageSenderRole } from "../../common/types/Roles";
import { optimizeImageUrl } from "../../utils/Storyblok.image.util";
import { formatChatMessageDate, localDateToDayDateString } from "../../utils/time.util";
import RenderLinksWithTooltips from "../Common/RenderLinksWithTooltips";
import "./ChatMessageBubble.css";
import { EventEmitter } from "../../EventEmitter";
import routes from "../../utils/routes";
import { Flex } from "../Common/Flex";
import Icon, { PictogramIcon } from "../Common/Icon";
import EmojiReaction from "./EmojiReaction";
import MessageAttachmentPreviews from "./MessageAttachmentPreviews";

function getArticleInquiry(message: unknown) {
    const parseResult = MessageWithArticleInquiry.safeParse(message);
    return parseResult.success ? parseResult.data.articleInquiry : undefined;
}

function getQuickRequest(message: unknown) {
    const parseResult = MessageWithQuickRequest.safeParse(message);
    return parseResult.success ? parseResult.data.quickRequest : undefined;
}

function ReplyToMessageImage(props: { replyTo: ReplyToMessage }) {
    const { replyTo } = props;
    const { attachments, bookingSuggestions, confirmedBooking, articles } =
        replyTo.replyToMessage;

    const hasAttachments = attachments && attachments.length > 0;
    const hasBookingSuggestions = bookingSuggestions && bookingSuggestions.length > 0;
    const hasArticles = articles && articles.length > 0;

    if (!hasAttachments && !hasBookingSuggestions && !confirmedBooking && !hasArticles)
        return <div />;

    const isPdf =
        !hasBookingSuggestions &&
        attachments &&
        attachments.length > 0 &&
        attachments[0].mimeType === "application/pdf";

    const attachmentImageUrl =
        hasAttachments && attachments[0] && !isPdf && attachments[0].url;
    const bookingSuggestionImageUrl =
        hasBookingSuggestions && bookingSuggestions[0]?.imageURL;
    const confirmedBookingImageUrl = confirmedBooking?.imageURL;
    const articleImageFilename = articles?.[0]?.content.coverImage?.filename;
    const articleImageUrl =
        articleImageFilename &&
        optimizeImageUrl(articleImageFilename, { width: 50, height: 50 });
    const imageUrl =
        confirmedBookingImageUrl ||
        bookingSuggestionImageUrl ||
        articleImageUrl ||
        attachmentImageUrl;
    return imageUrl ? (
        <img src={imageUrl || ""} alt="" />
    ) : (
        <PictogramIcon.Description.Medium.S90 />
    );
}

function ReplyToBubble(props: { replyTo: ReplyToMessage; memberId: string }) {
    const { replyTo, memberId } = props;

    return (
        <Link
            to={routes.chatMessage(
                memberId,
                replyTo.replyToMessageId,
                replyTo.replyToMessage.request || undefined,
            )}
            onClick={() => {
                // Emitter call is needed so that scrolling into chat message works also within same url route
                EventEmitter.emit({
                    name: "scrollChatIntoMessage",
                    params: { messageId: replyTo.replyToMessageId },
                });
            }}
            className="chat-message__reply-to-message chat-message__reply-to-message--with-file-attachments"
        >
            <ReplyToMessageImage replyTo={replyTo} />
            <div>
                <div className="chat-message__reply-to-message__label">
                    Reply
                    {replyTo.replyToMessage.requestTitle &&
                        `: ${replyTo.replyToMessage.requestTitle}`}
                </div>
                <div className="chat-message__reply-to-message__message">
                    {replyTo.replyToMessage.message}
                </div>
            </div>
        </Link>
    );
}
function QuickRequestBubble(props: { quickRequest: QuickRequest }) {
    const { quickRequest } = props;
    return (
        <div className="quick-request">
            <div className="quick-request-title">{quickRequest.title}</div>
            <div className="quick-request-details">
                <div className="quick-request-detail-row">
                    <span className="quick-request-detail__title">Type</span>
                    <span>
                        {QuickRequestTypeLabels[quickRequest.type] || "Unknown"}
                    </span>
                </div>

                {quickRequest.type === "restaurant" && (
                    <>
                        <div className="quick-request-detail-row">
                            <span className="quick-request-detail__title">Time</span>
                            <span>
                                {quickRequest.startDate &&
                                    localDateToDayDateString(quickRequest.startDate)}
                                {quickRequest.startTime
                                    ? ` ${quickRequest.startTime}`
                                    : ""}
                            </span>
                        </div>
                        <div className="quick-request-detail-row">
                            <span className="quick-request-detail__title">Guests</span>
                            <span>{quickRequest.numberOfGuests}</span>
                        </div>
                        <div className="quick-request-questions">
                            {quickRequest.specificLocationAnswer && (
                                <div className="quick-request-detail-column">
                                    {quickRequest.specificLocationQuestion && (
                                        <div className="quick-request-detail__title">
                                            {quickRequest.specificLocationQuestion}
                                        </div>
                                    )}

                                    <span className="quick-request-detail__value">
                                        {`"${quickRequest.specificLocationAnswer}"`}
                                    </span>
                                </div>
                            )}
                            {quickRequest.desiredLocationAnswer && (
                                <div className="quick-request-detail-column">
                                    {quickRequest.desiredLocationQuestion && (
                                        <div className="quick-request-detail__title">
                                            {quickRequest.desiredLocationQuestion}
                                        </div>
                                    )}

                                    <span className="quick-request-detail__value">
                                        {`"${quickRequest.desiredLocationAnswer}"`}
                                    </span>
                                </div>
                            )}
                            {quickRequest.additionalInfoAnswer && (
                                <div className="quick-request-detail-column">
                                    {quickRequest.additionalInfoQuestion && (
                                        <div className="quick-request-detail__title">
                                            {quickRequest.additionalInfoQuestion}
                                        </div>
                                    )}

                                    <span className="quick-request-detail__value">
                                        {`"${quickRequest.additionalInfoAnswer}"`}
                                    </span>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

type ChatMessageBubbleProps = {
    message: MessageOrInternalNote;
    sender: MessageSenderRole;
    currentMember: MemberDataFirestoreRecord;
    /** Optional multi-select affordance, absolutely positioned against the bubble. */
    selectSlot?: ReactNode;
};

function ChatMessageBubble(props: ChatMessageBubbleProps) {
    const { message, sender, currentMember, selectSlot } = props;

    const attachments: Attachment[] =
        message.attachments || (message.attachment ? [message.attachment] : []);

    const bookingSuggestions = message.bookingSuggestions || [];
    const { confirmedBooking } = message;

    const quickRequest = getQuickRequest(message);
    const articleInquiry = getArticleInquiry(message);
    const { articles, articlesV2 } = message;
    const reaction = MemberReaction.safeParse(message);
    const replyTo = ReplyToMessage.safeParse(message);

    const legend =
        quickRequest && message.request ? (
            <Link
                to={routes.requestWithMessage(message.request, message.id)}
                className="quick-request-link no-hover"
            >
                <Flex.Row gap="medium" alignItems="center" justifyContent="center">
                    <span>Quick request</span>
                    <Icon.OpenInNew.XSmall />
                </Flex.Row>
            </Link>
        ) : message.nomination ? (
            <span>Nomination</span>
        ) : null;

    return (
        <div
            className={classNames(
                "chat-message__bubble",
                `chat-message__bubble--${sender}`,
                {
                    "chat-message__bubble-with-legend": !!legend,
                    "chat-message__bubble-with-reaction": reaction.success,
                },
            )}
        >
            {selectSlot}
            {legend && (
                <div className="chat-message__legend-container">
                    <div className="chat-message__legend">{legend}</div>
                </div>
            )}

            {replyTo.success && (
                <ReplyToBubble replyTo={replyTo.data} memberId={currentMember.id} />
            )}

            {quickRequest && <QuickRequestBubble quickRequest={quickRequest} />}

            <MessageAttachmentPreviews
                currentMember={currentMember}
                persistedMessageDownload={{
                    messageId: message.id,
                }}
                displayBigImages
                displayBookingInOverlay
                attachments={attachments}
                articleInquiry={articleInquiry}
                articles={articles}
                articlesV2={articlesV2}
                bookingsTitle={message.requestTitle}
                bookings={[...(bookingSuggestions || [])]}
                confirmedBooking={confirmedBooking}
                displayDivider={message.message.length !== 0}
                nomination={
                    message.nomination
                        ? {
                              ...message.nomination,
                              message: message.message,
                          }
                        : undefined
                }
                request={message.request ?? undefined}
            />

            {message.message && !message.nomination ? (
                <RenderLinksWithTooltips referenceQuerySelector=".chat">
                    {message.sentByAgent && message.messageHtml ? (
                        <span
                            dangerouslySetInnerHTML={{
                                __html: message.messageHtml,
                            }}
                        />
                    ) : (
                        message.message
                    )}
                </RenderLinksWithTooltips>
            ) : null}

            {reaction.success && (
                <EmojiReaction
                    emoji={reaction.data.memberReaction}
                    tooltip={`${
                        currentMember.firstName
                    } reacted ${formatChatMessageDate(
                        reaction.data.memberReactionTimestamp,
                    )}`}
                />
            )}
        </div>
    );
}

export default ChatMessageBubble;
